import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ClipboardList, Edit, Search } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingManagement = () => {
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    purchaseStatus: 'Not Started',
    whatsappStatus: 'Not Started',
    websiteStatus: 'Not Started',
    campaignStatus: 'Not Started',
    adsStatus: 'Not Started',
    websiteLink: '',
    adsLiveDate: '',
    adsOverDate: ''
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = async (user) => {
    setSelectedUser(user);
    try {
      const response = await axios.get(`${API}/onboarding/${user.principalId}`);
      if (response.data) {
        setFormData({
          purchaseStatus: response.data.purchaseStatus || 'Not Started',
          whatsappStatus: response.data.whatsappStatus || 'Not Started',
          websiteStatus: response.data.websiteStatus || 'Not Started',
          campaignStatus: response.data.campaignStatus || 'Not Started',
          adsStatus: response.data.adsStatus || 'Not Started',
          websiteLink: response.data.websiteLink || '',
          adsLiveDate: response.data.adsLiveDate ? new Date(response.data.adsLiveDate).toISOString().slice(0, 10) : '',
          adsOverDate: response.data.adsOverDate ? new Date(response.data.adsOverDate).toISOString().slice(0, 10) : ''
        });
      }
    } catch (error) {
      // Use empty form
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        purchaseStatus: formData.purchaseStatus,
        whatsappStatus: formData.whatsappStatus,
        websiteStatus: formData.websiteStatus,
        campaignStatus: formData.campaignStatus,
        adsStatus: formData.adsStatus,
        websiteLink: formData.websiteLink,
        adsLiveDate: formData.adsLiveDate ? new Date(formData.adsLiveDate).toISOString() : null,
        adsOverDate: formData.adsOverDate ? new Date(formData.adsOverDate).toISOString() : null
      };
      await axios.put(`${API}/admin/onboarding/${selectedUser.principalId}`, data);
      toast({ title: 'Success', description: 'Onboarding progress updated successfully' });
      setDialogOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Update failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Onboarding Progress Management</h2>

      {/* Search Bar */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by Name, Email, Client ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {users.filter(user => {
          const search = searchTerm.toLowerCase();
          return (
            user.name?.toLowerCase().includes(search) ||
            user.email?.toLowerCase().includes(search) ||
            user.clientId?.toString().includes(search)
          );
        }).map(user => (
          <Card key={user.principalId} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800">#{user.clientId} - {user.name}</p>
                  <p className="text-sm text-slate-500">{user.email}</p>
                  <p className="text-xs text-slate-400 mt-1">Plan: {user.activePlan || 'None'}</p>
                </div>
                <Button size="sm" onClick={() => handleEdit(user)} className="bg-gradient-to-r from-blue-400 to-blue-700 hover:from-cyan-600 hover:to-blue-700">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Onboarding
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-cyan-400">Edit Onboarding Progress</DialogTitle>
            {selectedUser && (
              <p className="text-sm text-slate-500">#{selectedUser.clientId} - {selectedUser.name}</p>
            )}
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600">Purchase Status</Label>
                <Select value={formData.purchaseStatus} onValueChange={(value) => setFormData({...formData, purchaseStatus: value})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600">WhatsApp Status</Label>
                <Select value={formData.whatsappStatus} onValueChange={(value) => setFormData({...formData, whatsappStatus: value})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600">Website Status</Label>
                <Select value={formData.websiteStatus} onValueChange={(value) => setFormData({...formData, websiteStatus: value})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600">Campaign Status</Label>
                <Select value={formData.campaignStatus} onValueChange={(value) => setFormData({...formData, campaignStatus: value})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600">Ads Status</Label>
                <Select value={formData.adsStatus} onValueChange={(value) => setFormData({...formData, adsStatus: value})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="Live">Live</SelectItem>
                    <SelectItem value="Over">Over</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-600">Website Link</Label>
              <Input
                value={formData.websiteLink}
                onChange={(e) => setFormData({...formData, websiteLink: e.target.value})}
                placeholder="https://store.myshopify.com"
                className="bg-slate-50 border-slate-200 text-slate-800"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600">Ads Live Date</Label>
                <Input
                  type="date"
                  value={formData.adsLiveDate}
                  onChange={(e) => setFormData({...formData, adsLiveDate: e.target.value})}
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
              <div>
                <Label className="text-slate-600">Ads Over Date</Label>
                <Input
                  type="date"
                  value={formData.adsOverDate}
                  onChange={(e) => setFormData({...formData, adsOverDate: e.target.value})}
                  className="bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-blue-400 to-blue-700 hover:from-cyan-600 hover:to-blue-700">
              Update Onboarding Progress
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingManagement;
